// Moved by `git mv` from
// `src/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession.ts`,
// which survives as a delegate — so `git log --follow` on THIS path stops at the
// move and the pre-move history is `git log --follow` on the source path. Its
// suite stayed at the old path, where it drives the whole 409 recovery.
//
// TIER T2 (PORTS), AND ONE OF THE TWO IDENTITIES THE OLD FILE DECLARED DID NOT
// COME WITH IT. That is the rule, not an omission: a declaration moves only if
// its declaring hook does, and `GET_INTEGRATION_LINKED_SESSION_INFO` does not
// belong to this mutation at all — it is an ad-hoc `fetchQuery` identity minted
// inside the 409 recovery, which runs on the app's own `reactQueryClient`
// singleton and stays app-side with it. So only
// `POST_INTEGRATION_WORKFLOW_SESSION_MUTATION_KEY` is behind the door.
//
// NOTHING BUT THE REQUEST AND THE KEY MOVED, and the reason is worth stating
// because the delegate looks unusually heavy: the sequence around this call
// needs FIVE app dependencies and not one of them is the request —
// `invalidateSessionsQuery`, `invalidateIntegrationsMixinGetScheduleQuery`, the
// app's own `reactQueryClient` singleton, `useBuildSessionPath` and the router.
// The router is the one people expect to be the blocker; it is one of five, and
// it does not need a navigate port, because the whole sequence stays at the
// composition point that already owns all five.
//
// THE `useMutationState` WRAPPER ALSO STAYS APP-SIDE. It is the exported half of
// this module and it reads the key from `../../contracts/queryKeys` through the
// old path's re-export, so its filter still matches this mutation.
import { useMutation } from '@tanstack/react-query';

import { ehrMutationKeys } from '../../contracts/queryKeys';
import type { CreateIntegrationSessionWorkflowRequest } from '../../contracts/types';
import { createIntegrationWorkflowSession } from '../api/session-links';

type Params = {
  /**
   * Fired where the invalidations and the router push fired. AWAITED, because
   * the pre-carve `onSuccess` was `async` and awaited both the sessions
   * invalidation and the navigation.
   */
  onWorkflowSessionCreated?: (
    result: Awaited<ReturnType<typeof createIntegrationWorkflowSession>>
  ) => Promise<void> | void;
  /**
   * Fired for every rejection, with the variables. AWAITED for the same reason:
   * the pre-carve `onError` was `async` and the 409 recovery awaits a
   * `fetchQuery` and a second navigation inside it.
   */
  onCreateWorkflowSessionError?: (
    error: Error,
    variables: CreateIntegrationSessionWorkflowRequest
  ) => Promise<void> | void;
};

/**
 * Creates a Heidi session from an EHR appointment
 * (`POST /integrations/workflows/session`).
 *
 * THE ENVELOPE IS RETURNED, NOT THE BODY, transcribed: the delegate reads
 * `result.data.session.session_id` off it to build the destination path, and the
 * exported state wrapper hands `data` straight to its callers.
 */
export function useMutatePostIntegrationWorkflowSession({
  onWorkflowSessionCreated,
  onCreateWorkflowSessionError,
}: Params = {}) {
  return useMutation({
    mutationKey: ehrMutationKeys.postIntegrationWorkflowSession(),
    mutationFn: async (request: CreateIntegrationSessionWorkflowRequest) => {
      // Web reads the patient's demographics AFTER the session loads (see the
      // `patient_demographics_pending` reaction in the app-side delegate), so
      // the backend must not spend a vendor round trip on them here. This is a
      // client capability declaration, not a business rule: mobile clients that
      // do not send it keep the backend's inline read and identical stored data.
      //
      // CONSUMERS OF THIS HOOK TAKE ON AN OBLIGATION. Because this field is
      // always sent, the backend skips its own vendor read for Veradigm
      // patients and leaves the linked profile with a name and vendor ID only.
      // Whoever supplies `onWorkflowSessionCreated` MUST, when the response
      // carries `patient_demographics_pending: true`, call
      // `hydrateIntegrationPatientDemographics({ patientProfileId })` and
      // refresh the session, patient-profile and session-list caches on
      // `hydrated: true`. The app-side delegate at
      // `src/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession.ts`
      // is the reference implementation; a consumer that skips it ships
      // profiles with no date of birth or sex until the patient's next session.
      return await createIntegrationWorkflowSession({
        ...request,
        client_hydrates_patient_demographics: true,
      });
    },
    onSuccess: async (result) => {
      await onWorkflowSessionCreated?.(result);
    },
    onError: async (error, variables) => {
      await onCreateWorkflowSessionError?.(error, variables);
    },
  });
}
